import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router";
import validator from "validator";
import { useTranslate } from "@tolgee/react";

import PrimaryButton from "../Components/GenericComponents/PrimaryButton";
import SpinnerComponent from "../Components/GenericComponents/SpinnerComponent";
import { useNotify } from "../Helpers/Notifications";
import heroImage from "../Assets/img/emotii-header.png";
import { useConferencesService } from "../Context/ConferenceContext";
import useInAppBrowserEscape from "../hooks/useInAppBrowserEscape";
import InAppBrowserBanner from "../Components/GenericComponents/InAppBrowserBanner";

const ConferenceRegistrationPage = () => {
  const { id: routeId } = useParams();
  const { notifySuccess, notifyError } = useNotify();
  const { t } = useTranslate();
  const { getPublicConferenceDetails, registerPublicConference } = useConferencesService();
  const { showBanner: showInAppBrowserBanner } = useInAppBrowserEscape();
  const registrationStorageKey = useMemo(
    () => (routeId ? `conference_${routeId}_registered` : null),
    [routeId]
  );
  const [hasRegistered, setHasRegistered] = useState(() => {
    if (typeof window === "undefined" || !registrationStorageKey) return false;
    try {
      return window.localStorage.getItem(registrationStorageKey) === "true";
    } catch {
      return false;
    }
  });
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [conference, setConference] = useState(null);
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    acceptedTerms: false,
  });
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const notifyErrorRef = useRef(notifyError);

  useEffect(() => {
    notifyErrorRef.current = notifyError;
  }, [notifyError]);

  useEffect(() => {
    if (!registrationStorageKey || typeof window === "undefined") {
      setHasRegistered(false);
      return;
    }
    try {
      const storedFlag = window.localStorage.getItem(registrationStorageKey) === "true";
      setHasRegistered(storedFlag);
      if (storedFlag) setShowForm(false);
    } catch {
      setHasRegistered(false);
    }
  }, [registrationStorageKey]);

  useEffect(() => {
    setShowForm(false);
  }, [routeId]);

  useEffect(() => {
    if (!routeId) {
      setIsLoading(false);
      return;
    }
    getPublicConferenceDetails(
      routeId,
      (response) => {
        const conf = response?.data?.conference;
        setConference(conf || null);
        setIsLoading(false);
      },
      () => {
        notifyErrorRef.current(t("general.something_wrong"));
        setIsLoading(false);
      }
    );
  }, [routeId, getPublicConferenceDetails, t]);

  const logoSrc = useMemo(
    () => `${process.env.PUBLIC_URL || ""}/icons/emotii-new-logo.svg`,
    []
  );

  const startDate = conference?.start_at ? new Date(conference.start_at) : null;
  const endDate = conference?.end_at ? new Date(conference.end_at) : null;

  const formattedDateTime = useMemo(() => {
    if (!startDate || Number.isNaN(startDate.getTime())) return "";
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "full",
      timeStyle: "short",
    }).format(startDate);
  }, [startDate]);

  const formattedDuration = useMemo(() => {
    if (!startDate || !endDate) return "";
    const diffMinutes = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
    if (Number.isNaN(diffMinutes) || diffMinutes <= 0) return "";
    return `${diffMinutes} min`;
  }, [startDate, endDate]);

  const heroBg = conference?.header_image || conference?.hero || heroImage;
  const confTitle = conference?.title || t("general.conferences");
  const confDescription = conference?.description || "";
  const confTermsAndConditions = conference?.terms_and_conditions || "";
  const presenters = useMemo(() => {
    if (!conference?.presenters) return [];
    return conference.presenters
      .filter((p) => p.name)
      .map((p) => p.name);
  }, [conference?.presenters, t]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formValues.firstName.trim()) newErrors.firstName = t("conferences.first_name_required");
    if (!formValues.lastName.trim()) newErrors.lastName = t("conferences.surname_required");
    if (!validator.isEmail(formValues.email)) newErrors.email = t("conferences.email_valid");
    if (!formValues.acceptedTerms) newErrors.acceptedTerms = t("conferences.terms_required");
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    setErrors(formErrors);
    if (Object.keys(formErrors).length === 0) {
      setIsSubmitting(true);
      registerPublicConference(
        routeId,
        formValues.firstName.trim(),
        formValues.lastName.trim(),
        formValues.email.trim(),
        () => {
          notifySuccess(t("conferences.registered_success"));
          if (registrationStorageKey && typeof window !== "undefined") {
            try {
              window.localStorage.setItem(registrationStorageKey, "true");
              setHasRegistered(true);
            } catch {
              setHasRegistered(true);
            }
          } else {
            setHasRegistered(true);
          }
          setFormValues({
            firstName: "",
            lastName: "",
            email: "",
            acceptedTerms: false,
          });
          setShowForm(false);
          setIsSubmitting(false);
        },
        (err) => {
          notifyErrorRef.current(
            err?.response?.data?.error || t("general.something_wrong")
          );
          setIsSubmitting(false);
        }
      );
    }
  };

  const scrollToForm = () => {
    setShowForm(true);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const initials = (name) =>
    name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const formDisabled =
    !formValues.firstName.trim() ||
    !formValues.lastName.trim() ||
    !validator.isEmail(formValues.email) ||
    !formValues.acceptedTerms;

  return (
    <SpinnerComponent isLoading={isLoading}>
      <Page>
        {showInAppBrowserBanner && <InAppBrowserBanner />}
        <Shell>
          <HeroCard>
            <HeroImage src={heroBg} alt={confTitle} loading="lazy" />
          </HeroCard>

        <Stack>
          <Card>
            <CardBody>
              <HeroContent>
                <HeroHeader>
                  <div>
                    <LogoRow>
                      <LogoCircle>
                        <img src={logoSrc} alt="Emotii logo" />
                      </LogoCircle>
                      <div>
                        <HeroTitle>{confTitle}</HeroTitle>
                      </div>
                    </LogoRow>
                    <HeroMeta>
                      {formattedDateTime && <MetaText>{formattedDateTime}</MetaText>}
                      {formattedDateTime && formattedDuration && <Dot />}
                    {formattedDuration && <MetaText>{formattedDuration}</MetaText>}
                  </HeroMeta>
                  {confDescription && <HeroDescription>{confDescription}</HeroDescription>}
                </div>
              </HeroHeader>
            </HeroContent>
          </CardBody>
        </Card>

          <Card>
            <CardBody>
              <SectionTitle>{t("conferences.presenters")}</SectionTitle>
              <SpeakerList>
                {presenters.map((presenter) => (
                  <Speaker key={presenter}>
                    <Avatar>{initials(presenter)}</Avatar>
                    <div>
                      <SpeakerName>{presenter}</SpeakerName>
                    </div>
                  </Speaker>
                ))}
              </SpeakerList>
            </CardBody>
          </Card>

          {!showForm && (
            <CTAWrapper>
              {hasRegistered ? (
                <RegisteredText>{t("conferences.already_registered") || "Already registered"}</RegisteredText>
              ) : (
                <PrimaryButton onClick={scrollToForm} style={{ padding: "14px 18px" }}>
                  {t("conferences.register_now")}
                </PrimaryButton>
              )}
            </CTAWrapper>
          )}

          {showForm && (
            <FormCard ref={formRef}>
              <FormTop>
                <FormHeading>{t("conferences.reserve_seat")}</FormHeading>
              </FormTop>

              <form onSubmit={handleSubmit}>
                <FieldRow>
                  <Field>
                    <Label htmlFor="firstName">{t("conferences.first_name")}</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formValues.firstName}
                      onChange={handleInputChange}
                      placeholder="Jane"
                    />
                    {errors.firstName && <Error>{errors.firstName}</Error>}
                  </Field>
                  <Field>
                    <Label htmlFor="lastName">{t("conferences.surname")}</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formValues.lastName}
                      onChange={handleInputChange}
                      placeholder="Doe"
                    />
                    {errors.lastName && <Error>{errors.lastName}</Error>}
                  </Field>
                </FieldRow>

                <Field>
                  <Label htmlFor="email">{t("conferences.email")}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com"
                  />
                  {errors.email && <Error>{errors.email}</Error>}
                </Field>

                {confTermsAndConditions.trim().length > 0 && (
                  <TermsSection>
                    <TermsTitle>{t("general.terms_and_conditions")}</TermsTitle>
                    <TermsBody>{confTermsAndConditions}</TermsBody>
                  </TermsSection>
                )}

                <CheckboxRow>
                  <Checkbox
                    id="acceptedTerms"
                    name="acceptedTerms"
                    type="checkbox"
                    checked={formValues.acceptedTerms}
                    onChange={handleInputChange}
                  />
                  <CheckboxLabel htmlFor="acceptedTerms">
                    {t("conferences.terms_agreement")}
                  </CheckboxLabel>
                </CheckboxRow>
                {errors.acceptedTerms && <Error>{errors.acceptedTerms}</Error>}

                <ButtonRow>
                  <PrimaryButton type="submit" disabled={formDisabled || isSubmitting}>
                    {isSubmitting ? t("general.loading") : t("conferences.register")}
                  </PrimaryButton>
                  <GhostButton
                    type="button"
                    onClick={() => {
                      setFormValues({ firstName: "", lastName: "", email: "", acceptedTerms: false });
                      setShowForm(false);
                    }}
                  >
                    {t("conferences.cancel")}
                  </GhostButton>
                </ButtonRow>
              </form>
            </FormCard>
          )}
        </Stack>
      </Shell>
      </Page>
    </SpinnerComponent>
  );
};

export default ConferenceRegistrationPage;

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  background: ${({ theme }) => theme.menu.background};
  color: ${({ theme }) => theme.menu.primaryTextColor};
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0 12px 32px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const Shell = styled.div`
  width: 100%;
  max-width: 1080px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TermsSection = styled.section`
  margin-top: 14px;
  padding: 12px;
  border-radius: 12px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border: 1px solid ${({ theme }) => theme.menu.borderColor || "rgba(255,255,255,0.06)"};
`;

const TermsTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
`;

const TermsBody = styled.div`
  white-space: pre-wrap;
  line-height: 1.4;
  max-height: 260px;
  overflow: auto;
  opacity: 0.9;
`;

const HeroCard = styled.header`
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  color: #fff;
  aspect-ratio: 4 / 1;
  min-height: 160px;
  max-height: 320px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  box-shadow: 0 18px 40px rgba(10, 12, 19, 0.35);

  @media (max-width: 640px) {
    aspect-ratio: auto;
    min-height: unset;
    max-height: none;
    border-radius: 12px;
    background: ${({ theme }) => theme.menu.background};
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
  border-radius: inherit;

  background: ${({ theme }) => theme.menu.background};

  @media (max-width: 640px) {
    height: auto;
  }
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoCircle = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.16);
  display: grid;
  place-items: center;
  img {
    width: 34px;
    height: 34px;
    object-fit: contain;
  }
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  letter-spacing: 0.2px;

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const HeroMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-weight: 600;
`;

const MetaText = styled.span`
  font-size: 16px;
  opacity: 0.95;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
`;

const HeroHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeroDescription = styled.p`
  margin: 0;
  line-height: 1.6;
  color: ${({ theme }) => theme.menu.secondaryTextColor};

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.section`
  background: ${({ theme }) => theme.chat.conversationBackground};
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(17, 19, 26, 0.08);
`;

const CardBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const SpeakerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Speaker = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 10px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0fb8b7, #5ee7df);
  color: #0c2d36;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.2px;

  @media (max-width: 640px) {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    font-size: 14px;
  }
`;

const SpeakerName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const CTAWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 4px 0 4px 0;

  @media (max-width: 640px) {
    justify-content: center;
  }
`;

const RegisteredText = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  padding: 12px 0;
`;

const FormCard = styled.section`
  background: ${({ theme }) => theme.chat.conversationBackground};
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(17, 19, 26, 0.08);
  padding: 20px;

  @media (max-width: 640px) {
    padding: 16px 14px;
  }
`;

const FormTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

const FormHeading = styled.h3`
  margin: 0;
  font-size: 20px;
  color: ${({ theme }) => theme.menu.primaryTextColor};

  @media (max-width: 640px) {
    font-size: 18px;
  }
`;


const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

const Label = styled.label`
  font-size: 14px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.background};
  color: ${({ theme }) => theme.menu.primaryTextColor};
  border-radius: 8px;
  padding: 12px;
  font-size: 15px;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.menu.selectedTextColor};
    outline-offset: 1px;
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 4px 0 10px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.menu.selectedTextColor};
`;

const CheckboxLabel = styled.label`
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 12px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: stretch;

    button {
      flex: 1 1 100%;
      min-height: 48px;
      padding: 14px 16px;
    }
  }
`;

const GhostButton = styled.button`
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  font-weight: 500;
  cursor: pointer;
`;

const Error = styled.div`
  color: ${({ theme }) => theme.menu.deleteColor};
  font-size: 12px;
`;
